import { Global } from "@emotion/react";
import { colord, extend } from "colord";
import { Helmet } from "react-helmet-async";
import a11yPlugin from "colord/plugins/a11y";
import type { AppConfigGetResponseBody } from "../api/index.ts";

import * as backgrounds from "../assets/backgrounds/index.ts";
import { getUploadURL } from "../util/index.ts";
import {
  getTintedBackgroundColorDisabled,
  getTintedBackgroundColorVeryLight,
  getTintedBorderColor,
  getTintedBorderColorActive,
  getTintedBorderColorDisabled,
  getTintedReadableColor,
  getTintedReadableDisabledColor,
  getTintedReadableMutedColor,
} from "./tinted-colors.ts";

extend([a11yPlugin]);

export const ThemeVariables = ({
  sport,
  theme,
}: {
  sport: string;
  theme: AppConfigGetResponseBody["theme"];
}) => (
  <>
    <Helmet>
      <meta
        name="theme-color"
        media="(prefers-color-scheme: light)"
        content={theme.brandBackgroundColor}
      />
      <meta
        name="theme-color"
        media="(prefers-color-scheme: dark)"
        content={theme.brandBackgroundColor}
      />
    </Helmet>
    <Global
      styles={`
      :root {
        --readable-color-on-dark: #FFFFFF;
        --readable-color-muted-on-dark: rgba(255,255,255,0.64);
        --readable-color-disabled-on-dark: rgba(255,255,255,0.5);
        --readable-color-on-light: #343536;
        --readable-color-muted-on-light: #666768;
        --readable-color-disabled-on-light: #BBBBBB;

        --readable-color: var(--readable-color-on-light);
        --readable-color-muted: var(--readable-color-muted-on-light);
        --readable-color-disabled: var(--readable-color-disabled-on-light);

        --background-color-disabled: var(--readable-color-muted);
        --border-color-active: var(--readable-color);
        --border-color-disabled: var(--readable-color-disabled);

        --theme-background-image-url: ${getBackgroundImageURL(theme, sport)};
        --theme-background-image-placeholder-url: ${getBackgroundPlaceholderImageURL(
          theme,
          sport,
        )};
        --theme-background-color: ${theme.brandBackgroundColor};
        --theme-background-color-opacity-85: ${colord(
          theme.brandBackgroundColor,
        )
          .alpha(0.85)
          .toHex()};
        --theme-background-color-opacity-90: ${colord(
          theme.brandBackgroundColor,
        )
          .alpha(0.9)
          .toHex()};
        --theme-background-readable-color: ${getTintedReadableColor(
          theme.brandBackgroundColor,
        )};
        --theme-background-readable-color-muted: ${getTintedReadableMutedColor(
          theme.brandBackgroundColor,
        )};
        --theme-background-readable-color-disabled: ${getTintedReadableDisabledColor(
          theme.brandBackgroundColor,
        )};
        --theme-background-color-disabled: ${getTintedBackgroundColorDisabled(
          theme.brandBackgroundColor,
        )};
        --theme-background-color-very-light: ${getTintedBackgroundColorVeryLight(
          theme.brandBackgroundColor,
        )};
        --theme-background-border-color: ${getTintedBorderColor(
          theme.brandBackgroundColor,
        )};
        --theme-background-border-color-active: ${getTintedBorderColorActive(
          theme.brandBackgroundColor,
        )};
        --theme-background-border-color-disabled: ${getTintedBorderColorDisabled(
          theme.brandBackgroundColor,
        )};

        --theme-foreground-color: ${theme.brandForegroundColor};
        --theme-foreground-color-opacity-20: ${colord(
          theme.brandForegroundColor,
        )
          .alpha(0.2)
          .toHex()};
        --theme-foreground-readable-color: ${getTintedReadableColor(
          theme.brandForegroundColor,
        )};
        --theme-foreground-readable-color-muted: ${getTintedReadableMutedColor(
          theme.brandForegroundColor,
        )};
        --theme-foreground-readable-color-disabled: ${getTintedReadableDisabledColor(
          theme.brandForegroundColor,
        )};
        --theme-foreground-color-disabled: ${getTintedBackgroundColorDisabled(
          theme.brandBackgroundColor,
        )};
        --theme-foreground-color-very-light: ${getTintedBackgroundColorVeryLight(
          theme.brandForegroundColor,
        )};
        --theme-foreground-border-color: ${getTintedBorderColor(
          theme.brandForegroundColor,
        )};
        --theme-foreground-border-color-active: ${getTintedBorderColorActive(
          theme.brandForegroundColor,
        )};
        --theme-foreground-border-color-disabled: ${getTintedBorderColorDisabled(
          theme.brandForegroundColor,
        )};

        --theme-button-color: ${theme.brandButtonColor};
        --theme-button-readable-color: ${getTintedReadableColor(
          theme.brandButtonColor,
        )};
        --theme-button-readable-color-disabled: ${getTintedReadableDisabledColor(
          theme.brandButtonColor,
        )};
        --theme-link-color: ${theme.brandLinkColor};
        --theme-decoration-color: ${theme.brandDecorationColor};

        --theme-success-color: #3EC880;
        --theme-danger-color: #DA5922;

        --gutter-y: 1rem;
        --gutter-x: 1.5rem;

        @media screen and (min-width: 28rem) {
          --gutter-x: 2.5rem;
        }
      }

      *::-webkit-scrollbar {
        background: transparent;
        padding: 0.125rem;
        margin: 0.125rem;
      }

      *::-webkit-scrollbar-thumb {
        background: rgba(120 120 120 / 0.2);
        border-radius: 1000px;
        border: 0.1875rem solid transparent;
        background-clip: padding-box;
      }

      *::-webkit-scrollbar-track-piece
      {
         display:none;
      }
    `}
    />
  </>
);

export const readable = (color: string) =>
  colord("#FFFFFF").isReadable(color)
    ? "var(--readable-color-on-dark)"
    : "var(--readable-color-on-light)";

function getBackgroundImageURL(
  theme: AppConfigGetResponseBody["theme"],
  sport: string,
) {
  if (theme.backgroundImage) {
    return `url('${getUploadURL(theme.backgroundImage)}')`;
  } else {
    const IMAGE_URL =
      backgrounds[`BG_${sport.toUpperCase()}_URL` as keyof typeof backgrounds];

    if (IMAGE_URL) {
      return `url('${IMAGE_URL}')`;
    }
  }

  return "var(--transparent-gradient)";
}

function getBackgroundPlaceholderImageURL(
  theme: AppConfigGetResponseBody["theme"],
  sport: string,
) {
  if (theme.backgroundImage) {
    if (
      theme.backgroundImage.placeholder &&
      theme.backgroundImage.placeholderFormat
    ) {
      const source = theme.backgroundImage;

      return `url("data:${source.placeholderFormat};base64,${source.placeholder}")`;
    } else {
      return `var(--transparent-gradient, linear-gradient(0deg, transparent, transparent))`;
    }
  } else {
    const IMAGE_PLACEHOLDER =
      backgrounds[
        `BG_${sport.toUpperCase()}_PLACEHOLDER` as keyof typeof backgrounds
      ];

    if (IMAGE_PLACEHOLDER) {
      return `url('data:image/jpeg;base64,${IMAGE_PLACEHOLDER}')`;
    }
  }

  return `var(--transparent-gradient, linear-gradient(0deg, transparent, transparent))`;
}
